// THE LEAD SOURCE REGISTRY, CLIENT SIDE — a holder, not a copy (owner decisions 2026-11-02).
//
// The list lives on the server (crm-aro-backend/lead-sources.js) and arrives on
// GET /api/me as `leadSources` (boot phase 1), or from GET /api/lead-sources when
// that refresh fails. Nothing in src/ may spell a source list of its own: every
// picker, filter and colour reads from here, so adding a source on the server is
// the whole change. verify-source-normalization.js fails the build on a
// hard-coded list.
//
// Pages mount under the boot loader BEFORE /api/me answers, and some pickers are
// memo()'d, so a plain read at render time can be stuck on the empty list.
// Components that render source options call useLeadSourcesVersion(), which
// re-renders them when the list lands. The last list is kept in sessionStorage so
// a reload paints the same list on its first frame.
import { useSyncExternalStore } from "react";

var STORE_KEY = "crm_lead_sources";
var EMPTY = { sources: [], literals: [] };
var state = EMPTY;
var version = 0;
var listeners = [];

function shapeOk(p) {
  return !!p && Array.isArray(p.sources) && p.sources.every(function (s) { return s && typeof s.name === "string" && s.name; })
    && (p.literals == null || Array.isArray(p.literals));
}

// Apply the server's payload. A malformed one is ignored (the last good list stays).
export function setLeadSources(payload) {
  if (!shapeOk(payload)) return false;
  state = { sources: payload.sources.slice(), literals: (payload.literals || []).slice() };
  version++;
  try { sessionStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch (e) { /* storage unavailable */ }
  listeners.slice().forEach(function (fn) { fn(); });
  return true;
}

(function restore() {
  try {
    var raw = sessionStorage.getItem(STORE_KEY);
    var p = raw ? JSON.parse(raw) : null;
    if (shapeOk(p)) state = { sources: p.sources, literals: p.literals || [] };
  } catch (e) { /* first visit, or storage unavailable */ }
})();

function subscribe(fn) {
  listeners.push(fn);
  return function () { listeners = listeners.filter(function (x) { return x !== fn; }); };
}
function getVersion() { return version; }

export function useLeadSourcesVersion() {
  return useSyncExternalStore(subscribe, getVersion, getVersion);
}

// The canonical names, in registry order — what every picker offers.
export function leadSourceNames() {
  return state.sources.map(function (s) { return s.name; });
}

// "Daily Request" / "Daily Request → Lead": stored values no picker offers.
export function literalSourceNames() {
  return state.literals.map(function (s) { return s.name; });
}

// A paid-ad / listing platform per the registry.
export function isAdSource(name) {
  var n = String(name || "");
  return state.sources.some(function (s) { return s.name === n && s.isAd === true; });
}

// The registry colour for a source or literal, case-insensitive; null when the
// value is in neither (callers keep their own default for it).
export function leadSourceColor(name) {
  var k = String(name || "").trim().toLowerCase();
  if (!k) return null;
  var all = state.sources.concat(state.literals);
  for (var i = 0; i < all.length; i++) {
    if (String(all[i].name).toLowerCase() === k) return all[i].color || null;
  }
  return null;
}
